import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'

const COORDINATE_PRECISION = 9

/**
 * Thin wrapper around Terra Draw for editing a single building outline.
 */
export class DrawingBoard {
  constructor (map, callback, google) {
    this.map = map
    this.callback = callback
    this.google = google

    this.featureId = null
    this.syncing = false
    this.ready = false
    this.pendingPath = null
    this.pendingMode = null

    const div = this.map.getDiv()
    if (!div.id) {
      div.id = 'terraDrawMap'
    }

    this.terraDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map: this.map,
        coordinatePrecision: COORDINATE_PRECISION
      }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: '#f8f8f8',
            fillOpacity: 0.5,
            outlineColor: '#9d2f2f',
            outlineWidth: 2
          }
        }),
        new TerraDrawSelectMode({
          allowManualSelection: false,
          flags: {
            polygon: {
              feature: {
                draggable: true,
                coordinates: {
                  draggable: true,
                  midpoints: { draggable: true },
                  deletable: true
                }
              }
            }
          },
          styles: {
            selectedPolygonColor: '#f8f8f8',
            selectedPolygonFillOpacity: 0.5,
            selectedPolygonOutlineColor: '#9d2f2f',
            selectedPolygonOutlineWidth: 2,
            selectionPointColor: '#9d2f2f',
            selectionPointOutlineColor: '#ffffff',
            selectionPointWidth: 6,
            midPointColor: '#ffffff',
            midPointOutlineColor: '#9d2f2f',
            midPointWidth: 4
          }
        })
      ]
    })

    // 'finish' fires once when a polygon is completed and after every drag
    // of the feature, a vertex or a midpoint. Sync the shape back on those only,
    // so we don't emit half-drawn polygons.
    this.terraDraw.on('finish', (id) => {
      if (this.syncing) {
        return
      }
      const feature = this.terraDraw.getSnapshotFeature(id)
      if (feature?.geometry?.type === 'Polygon') {
        this.featureId = feature.id
        this.callback(JSON.stringify(this.geoJsonToLatLng(feature)))
        // Once drawn, drop into select mode so a second polygon can't be started.
        this.terraDraw.setMode('select')
      }
    })

    this.terraDraw.on('change', (ids, type) => {
      if (this.syncing || type !== 'delete') {
        return
      }
      if (this.featureId !== null && ids.includes(this.featureId)) {
        this.featureId = null
        this.callback(null)
      }
    })

    this.terraDraw.on('ready', () => {
      this.ready = true
      if (this.pendingPath !== null) {
        const path = this.pendingPath
        this.pendingPath = null
        this.draw(path)
      } else if (this.pendingMode !== null) {
        const mode = this.pendingMode
        this.pendingMode = null
        this.terraDraw.setMode(mode)
      }
    })

    this.terraDraw.start()
  }

  /** Remove any drawn polygon. Does not fire the callback. */
  clear () {
    this.pendingPath = null
    if (!this.ready) {
      return
    }
    this.syncing = true
    this.terraDraw.clear()
    this.syncing = false
    this.featureId = null
  }

  /** Let the user draw a new polygon. */
  showTools () {
    this.setMode('polygon')
  }

  /** Load an existing [{lat,lng}] JSON string as a locked polygon. */
  draw (path) {
    if (!this.ready) {
      this.pendingPath = path
      return
    }

    this.clear()

    let latLngs
    try {
      latLngs = JSON.parse(path)
    } catch (e) {
      console.error('DrawingBoard: shape is not valid JSON', e)
      return
    }
    if (!Array.isArray(latLngs) || latLngs.length < 3) {
      return
    }

    const feature = {
      type: 'Feature',
      properties: { mode: 'polygon' },
      geometry: {
        type: 'Polygon',
        coordinates: [this.latLngToGeoJson(latLngs)]
      }
    }

    this.syncing = true
    const validation = this.terraDraw.addFeatures([feature])
    this.syncing = false

    const result = validation && validation[0]
    if (result && result.valid === false) {
      console.error('DrawingBoard: unable to load polygon:', result.reason)
      return
    }

    this.featureId = (result && result.id) || this.terraDraw.getSnapshot()[0]?.id || null
    this.lock()
  }

  /** Make the loaded polygon draggable/editable. */
  edit () {
    if (this.featureId === null) {
      return
    }
    this.setMode('select')
    if (this.ready) {
      this.terraDraw.selectFeature(this.featureId)
    }
  }

  /** Stop editing; polygon stays visible but untouchable. */
  lock () {
    if (this.ready && this.featureId !== null) {
      try {
        this.terraDraw.deselectFeature(this.featureId)
      } catch (e) {
        // Not selected (nothing to do).
      }
    }
    this.setMode('select')
  }

  /** Alias kept for existing callers. */
  hideTools () {
    this.lock()
  }

  setMode (mode) {
    if (!this.ready) {
      this.pendingMode = mode
      return
    }
    this.terraDraw.setMode(mode)
  }

  destroy () {
    if (this.terraDraw.enabled) {
      this.terraDraw.stop()
    }
  }

  latLngToGeoJson (path) {
    const round = (n) => Number(Number(n).toFixed(COORDINATE_PRECISION))
    const coordinates = []
    path.forEach((point) => {
      const coord = [round(point.lng), round(point.lat)]
      const prev = coordinates[coordinates.length - 1]
      // Skip consecutive duplicates (rounding can create them).
      if (!prev || prev[0] !== coord[0] || prev[1] !== coord[1]) {
        coordinates.push(coord)
      }
    })
    const first = coordinates[0]
    const last = coordinates[coordinates.length - 1]
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push([first[0], first[1]])
    }
    return coordinates
  }

  geoJsonToLatLng (feature) {
    return feature.geometry.coordinates[0].slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
  }
}